"use client";

import React, { useEffect, useState } from "react";
import { FiltersProps } from "@/types/materialsLibrary";
import { Button, Card, Collapse, ConfigProvider, Flex, Form, Grid, Skeleton, Typography } from "antd/lib";
import XStroke from "@/public/icons/XStroke";
import { useTranslation } from "react-i18next";
import Title from "antd/lib/typography/Title";
import QueueAnim from "rc-queue-anim/lib/QueueAnim";
import filterConfig from "@/src/theme/filterConfig";
import { useMaterialsFetchers } from "@/hooks/useMaterialsFetchers";
import TagCategorySection from "@/src/components/Common/TagCategorySection";
import FilterWrapper from "@/src/components/Common/FilterWrapper";
import { SearchParams } from "@/constants/common";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const Filter: React.FC<FiltersProps> = ({
  form,
  isDrawerOpen,
  isLoading,
  applyFiltersHandler,
  resetFiltersHandler,
  setIsLoading,
  setIsDrawerOpen,
  setActivityLanguages,
  formValuesChanged,
  formValuesChangeHandler,
  filtersCleared,
  setFiltersOptionsError,
}) => {
  const { t } = useTranslation();
  const { fetchFilters } = useMaterialsFetchers();

  const [filterData, setFilterData] = useState<any>(null);
  const [minCertificationLevel, setMinCertificationLevel] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    const fetchData = async () => {
      let data: any;

      try {
        data = await fetchFilters(setIsLoading);
      } catch (err) {
        setFiltersOptionsError(true);
        throw err;
      }

      const filterData: any = {};

      Object.entries(data).forEach(([key, value]) => {
        filterData[`filter[${key}]`] = value;
      });

      setFilterData(filterData);
      setActivityLanguages(data.file_content_language_id);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  // const handleCertificationLevelChange = (checkedValues: any[]) => {
  //   setMinCertificationLevel(checkedValues);
  // };

  const handleCategoryChange = (selectedCategories: any[]) => {
    const categoryItems = filterData?.[SearchParams.filterMaterialFileTagCategory].items;

    const selectedLabels = categoryItems
      .filter((item: { label: string; value: any }) => selectedCategories.includes(item.value))
      .map((item: { label: string; value: any }) => item.label);

    setSelectedCategories(selectedLabels);
  };

  const clearAllFilters = () => {
    resetFiltersHandler();

    setMinCertificationLevel([]);
    setSelectedCategories([]);
  };

  const items = [
    /*
    filterData?.file_min_certification_level && {
      key: "1",
      label: (
        <Text strong style={{ fontSize: "16px" }}>
          Certification Levels
        </Text>
      ),
      children: (
        <Flex vertical gap={16}>
          <CertificationLevelsSelect
            certificationLevels={filterData?.file_min_certification_level}
            onChange={handleCertificationLevelChange}
          />
        </Flex>
      ),
    },
    (minCertificationLevel.length > 1 || (minCertificationLevel.length === 1 && minCertificationLevel[0] !== 0)) && {
      key: "2",
      label: (
        <Text strong style={{ fontSize: "16px" }}>
          Certification Areas
        </Text>
      ),
      children: (
        <Flex vertical gap={16}>
          <CertificationAreas certificationAreas={filterData?.file_certification_area_id} />
        </Flex>
      ),
    },
*/
    ...[SearchParams.filterMaterialFileTagAge, SearchParams.filterMaterialFileTagAccess]
      .filter((param) => filterData?.[param])
      .map((param, index) => ({
        key: `${index + 3}`,
        label: (
          <Text strong style={{ fontSize: "16px" }}>
            {filterData?.[param].label}
          </Text>
        ),
        children: <TagCategorySection name={param} items={filterData?.[param].items} onChange={handleCategoryChange} />,
      })),
    (minCertificationLevel.length > 1 || (minCertificationLevel.length === 1 && minCertificationLevel[0] !== 0)) &&
      filterData?.[SearchParams.filterMaterialFileTagCategory] && {
        key: "5",
        label: (
          <Text strong style={{ fontSize: "16px" }}>
            {filterData?.[SearchParams.filterMaterialFileTagCategory].label}
          </Text>
        ),
        children: (
          <TagCategorySection
            name={SearchParams.filterMaterialFileTagCategory}
            items={filterData?.[SearchParams.filterMaterialFileTagCategory].items}
            onChange={handleCategoryChange}
          />
        ),
      },
    selectedCategories.includes("Equipping") &&
      filterData?.[SearchParams.filterMaterialFileTagEquippingType] && {
        key: "6",
        label: (
          <Text strong style={{ fontSize: "16px" }}>
            {filterData?.[SearchParams.filterMaterialFileTagEquippingType].label}
          </Text>
        ),
        children: (
          <TagCategorySection
            name={SearchParams.filterMaterialFileTagEquippingType}
            items={filterData?.[SearchParams.filterMaterialFileTagEquippingType].items}
          />
        ),
      },
    filterData?.[SearchParams.filterMaterialFileContentLanguage] && {
      key: "7",
      label: (
        <Text strong style={{ fontSize: "16px" }}>
          {t("materialsLibraryPage.filters.languages")}
        </Text>
      ),
      children: (
        <Flex vertical>
          <TagCategorySection
            name={SearchParams.filterMaterialFileContentLanguage}
            items={filterData?.[SearchParams.filterMaterialFileContentLanguage]}
          />
          {/*<Button block className={styles.missingLanguageButton}>*/}
          {/*  {t("materialsLibraryPage.filters.reportMissingLanguage")}*/}
          {/*</Button>*/}
        </Flex>
      ),
    },
  ].filter(Boolean);

  return (
    <FilterWrapper isDrawerOpen={isDrawerOpen} setIsDrawerOpen={setIsDrawerOpen}>
      <ConfigProvider theme={filterConfig}>
        <div className="filter">
          <Form onFinish={applyFiltersHandler} onValuesChange={formValuesChangeHandler} form={form}>
            {formValues && (
              <Card>
                <Skeleton active loading={isLoading}>
                  <QueueAnim duration={1000} interval={100}>
                    <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 24 }}>
                      <Title level={5} style={{ margin: 0 }}>
                        {t("common.filters.title")}
                      </Title>
                      <Button block type="primary" htmlType="submit" disabled={!formValuesChanged}>
                        {t("common.filters.actions.apply")}
                      </Button>
                    </Flex>
                    <Button
                      disabled={filtersCleared}
                      type="text"
                      onClick={clearAllFilters}
                      style={{
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        marginBottom: 8,
                      }}
                      icon={<XStroke color={filtersCleared ? "#B0B0B0" : undefined} />}
                    >
                      {t("common.filters.actions.clear")}
                    </Button>
                    <Collapse
                      ghost
                      expandIconPosition="end"
                      style={{ width: "100%", padding: 0, margin: 0 }}
                      items={items}
                      defaultActiveKey={[1, 2, 3, 4, 5, 6, 7]}
                    />
                    <TagCategorySection
                      name={SearchParams.filterIsFavorite}
                      items={[{ label: t("materialsLibraryPage.filters.favorite"), value: "true" }]}
                    />
                  </QueueAnim>
                </Skeleton>
              </Card>
            )}
          </Form>
        </div>
      </ConfigProvider>
    </FilterWrapper>
  );
};

export default Filter;
